import * as React from 'react';
import { Box, Paper, IconButton, Typography, Badge, styled } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TradingButton from '../components/Trader/TradingButton';

// Styled Components
const FooterContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 20000,
  display: 'block',
  [theme.breakpoints.up('md')]: {
    display: 'none'
  }
}));

const FooterContent = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0B0E11 0%, #161A1E 50%, #1E2328 100%)',
  border: '1px solid #2B3139',
  borderBottom: 'none',
  borderRadius: '20px 20px 0 0',
  position: 'relative',
  height: '80px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
  paddingBottom: '8px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '6px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '40px',
    height: '4px',
    background: 'linear-gradient(90deg, #F7931A 0%, #FFB800 100%)',
    borderRadius: '2px',
    opacity: 0.6
  }
}));

const NavItem = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  padding: '8px 12px',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  minWidth: '60px',
  position: 'relative',
  '&:hover': {
    background: 'rgba(247, 147, 26, 0.1)',
    transform: 'translateY(-2px)'
  },
  ...(active && {
    background: 'rgba(247, 147, 26, 0.15)',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-4px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '20px',
      height: '2px',
      background: '#F7931A',
      borderRadius: '1px'
    }
  })
}));

const NavIcon = styled(IconButton)(({ theme, active }) => ({
  width: '40px',
  height: '40px',
  background: active ? 'rgba(247, 147, 26, 0.2)' : 'rgba(255, 255, 255, 0.05)',
  border: `1px solid ${active ? 'rgba(247, 147, 26, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
  color: active ? '#F7931A' : '#B7BDC6',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(247, 147, 26, 0.2)',
    border: '1px solid rgba(247, 147, 26, 0.4)',
    color: '#F7931A',
    transform: 'scale(1.05)'
  }
}));

const CenterHomeButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '-25px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '60px',
  height: '60px',
  background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)',
  boxShadow: '0 8px 24px rgba(247, 147, 26, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)',
  border: '3px solid #0B0E11',
  color: '#000',
  zIndex: 10,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #FFB800 0%, #F7931A 100%)',
    transform: 'translateX(-50%) translateY(-4px) scale(1.1)',
    boxShadow: '0 12px 32px rgba(247, 147, 26, 0.5), 0 6px 16px rgba(0, 0, 0, 0.4)'
  },
  '&:active': {
    transform: 'translateX(-50%) translateY(-2px) scale(1.05)'
  }
}));

const NavLabel = styled(Typography)(({ theme, active }) => ({
  fontSize: '10px',
  fontWeight: active ? 600 : 500,
  color: active ? '#F7931A' : '#B7BDC6',
  textAlign: 'center',
  transition: 'color 0.3s ease',
  maxWidth: '60px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

const NotificationBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    background: 'linear-gradient(135deg, #F84960 0%, #FF6B85 100%)',
    color: 'white',
    fontSize: '8px',
    fontWeight: 600,
    minWidth: '16px',
    height: '16px',
    top: '6px',
    right: '6px'
  }
}));

export default function Footer() {
  const location = useLocation();
  const { pathname } = location;

  const navItems = [
    {
      path: '/send-coin',
      icon: SendIcon,
      label: 'Withdrawal',
      hasNotification: false
    },
    {
      path: '/copy-plans',
      icon: ContentCopyIcon,
      label: 'Joint Invest',
      hasNotification: false
    },
    null, // Center space for home button
    {
                  path: '/package/Investment Portfolios',
      icon: SmartToyIcon,
      label: 'Fixed Investment',
      hasNotification: false
    },
    {
      path: '/assets',
      icon: AccountBalanceWalletIcon,
      label: 'Assets',
      hasNotification: false
    }
  ];

  if (pathname === '/trade') {
    return (
      <FooterContainer>
        <TradingButton />
      </FooterContainer>
    );
  }

  return (
    <FooterContainer>
      <FooterContent elevation={0}>
        {/* Center Home Button */}
        <CenterHomeButton
          component={RouterLink}
          to="/"
          aria-label="Home"
        >
          <HomeIcon sx={{ fontSize: '28px' }} />
        </CenterHomeButton>

        {/* Navigation Items */}
        {navItems.map((item, index) => {
          if (!item) {
            return <Box key={index} sx={{ width: '60px' }} />; // Spacer for center button
          }

          const Icon = item.icon;
          const isActive = pathname === item.path;

          return (
            <NavItem
              key={item.path}
              component={RouterLink}
              to={item.path}
              active={isActive}
            >
              {item.hasNotification ? (
                <NotificationBadge badgeContent={item.notificationCount || 0}>
                  <NavIcon active={isActive}>
                    <Icon sx={{ fontSize: '20px' }} />
                  </NavIcon>
                </NotificationBadge>
              ) : (
                <NavIcon active={isActive}>
                  <Icon sx={{ fontSize: '20px' }} />
                </NavIcon>
              )}
              <NavLabel active={isActive}>
                {item.label}
              </NavLabel>
            </NavItem>
          );
        })}
      </FooterContent>
    </FooterContainer>
  );
} 